import React, { useState, useEffect } from 'react';
import { X, Play, Edit3, FileText, Clock, CheckCircle, Info, Copy, Download } from 'lucide-react';
import { WorkflowDetails, workflowApi, sessionApi } from '../services/api';
import './WorkflowDetailsModal.css';

interface WorkflowDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  onExecute: () => void;
  onEditAsDraft: (newSessionId: string, workflowContext: any) => void;
}

export const WorkflowDetailsModal: React.FC<WorkflowDetailsModalProps> = ({
  isOpen,
  onClose,
  workflowId,
  onExecute,
  onEditAsDraft,
}) => {
  const [loading, setLoading] = useState(true);
  const [workflowDetails, setWorkflowDetails] = useState<WorkflowDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creatingDraft, setCreatingDraft] = useState(false);

  useEffect(() => {
    if (isOpen && workflowId) {
      fetchWorkflowDetails();
    }
  }, [isOpen, workflowId]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const fetchWorkflowDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await workflowApi.get(workflowId);
      setWorkflowDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch workflow details:', error);
      setError('Failed to load workflow details');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAsDraft = async () => {
    if (!workflowDetails) return;
    
    setCreatingDraft(true);
    try {
      // Create a new session for editing
      const sessionResponse = await sessionApi.create();
      const newSession = sessionResponse.data;
      
      // Prepare workflow context with original instructions
      const workflowContext = {
        parentWorkflowId: workflowId,
        workflowName: workflowDetails.workflow.name,
        originalInstructions: workflowDetails.content?.instructions_summary || '',
        fileRequirements: workflowDetails.content?.input_file_metadata || {},
        functionCategory: workflowDetails.workflow.function_category,
      };
      
      // Call the parent callback to handle navigation
      onEditAsDraft(newSession.session_id, workflowContext);
      onClose();
    } catch (error) {
      console.error('Failed to create draft session:', error);
      setError('Failed to create draft session');
    } finally {
      setCreatingDraft(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      // Handle various date formats from backend
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date string:', dateString);
        return 'Date unavailable';
      }
      
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date unavailable';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="workflow-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Workflow Details</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading workflow details...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-primary" onClick={fetchWorkflowDetails}>
                Retry
              </button>
            </div>
          ) : workflowDetails ? (
            <>
              {/* Workflow Info Section */}
              <div className="details-section">
                <h3>{workflowDetails.workflow.name}</h3>
                {workflowDetails.workflow.description && (
                  <p className="workflow-description">{workflowDetails.workflow.description}</p>
                )}
                
                <div className="workflow-meta">
                  <div className="meta-item">
                    <span className="meta-label">Status:</span>
                    <span className={`status-badge ${workflowDetails.workflow.status}`}>
                      {workflowDetails.workflow.status}
                    </span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Type:</span>
                    <span>{workflowDetails.workflow.type}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Version:</span>
                    <span>{workflowDetails.workflow.version}</span>
                  </div>
                  {workflowDetails.workflow.function_category && (
                    <div className="meta-item">
                      <span className="meta-label">Category:</span>
                      <span>{workflowDetails.workflow.function_category.name}</span>
                    </div>
                  )}
                </div>

                <div className="workflow-dates">
                  <div className="date-item">
                    <Clock size={16} />
                    <span>Created {formatDate(workflowDetails.workflow.created_at)}</span>
                  </div>
                  {workflowDetails.workflow.updated_at && workflowDetails.workflow.updated_at !== workflowDetails.workflow.created_at && (
                    <div className="date-item">
                      <Clock size={16} />
                      <span>Updated {formatDate(workflowDetails.workflow.updated_at)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Instructions Summary */}
              {workflowDetails.content?.instructions_summary && (
                <div className="details-section">
                  <h4>
                    <Info size={18} />
                    Workflow Instructions
                  </h4>
                  <div className="instructions-box">
                    <p>{workflowDetails.content.instructions_summary}</p>
                  </div>
                </div>
              )}

              {/* Script Information */}
              {workflowDetails.content?.scripts && workflowDetails.content.scripts.length > 0 && (
                <div className="details-section">
                  <h4>
                    <FileText size={18} />
                    Generated Scripts
                  </h4>
                  <div className="script-info">
                    <p>{workflowDetails.content.scripts.length} script(s) included</p>
                    {workflowDetails.content.estimated_runtime_seconds && (
                      <p className="runtime">
                        Estimated runtime: {workflowDetails.content.estimated_runtime_seconds}s
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* UAT Status */}
              {workflowDetails.uat_records && workflowDetails.uat_records.length > 0 && (
                <div className="details-section">
                  <h4>
                    <CheckCircle size={18} />
                    UAT Status
                  </h4>
                  {workflowDetails.uat_records.map((uat, index) => (
                    <div key={uat.id} className="uat-record">
                      <div className="uat-status">
                        <span className={`status-badge ${uat.status}`}>{uat.status}</span>
                        <span className="uat-date">{formatDate(uat.performed_at)}</span>
                      </div>
                      {uat.test_summary && <p className="uat-summary">{uat.test_summary}</p>}
                      {uat.file_name && (
                        <div className="uat-file">
                          <FileText size={16} />
                          <span className="file-name">{uat.file_name}</span>
                          {uat.file_url && (
                            <a 
                              href={`/api/files/download/${uat.file_id}`}
                              download={uat.file_name}
                              className="download-link"
                              title="Download UAT documentation"
                            >
                              <Download size={16} />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Execution Statistics */}
              <div className="details-section">
                <h4>Usage Statistics</h4>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-value">{workflowDetails.workflow.execution_count || 0}</span>
                    <span className="stat-label">Total Executions</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">
                      {workflowDetails.workflow.created_by === 'current_user' ? 'You' : workflowDetails.workflow.created_by}
                    </span>
                    <span className="stat-label">Created By</span>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          
          {workflowDetails && (
            <>
              <button
                className="btn btn-outline"
                onClick={handleEditAsDraft}
                disabled={creatingDraft}
              >
                {creatingDraft ? (
                  <>
                    <div className="loading-spinner small inline"></div>
                    <span>Creating Draft...</span>
                  </>
                ) : (
                  <>
                    <Edit3 size={16} />
                    <span>Edit as Draft</span>
                  </>
                )}
              </button>
              
              {workflowDetails.workflow.type === 'final' && 
               workflowDetails.workflow.status === 'published' && (
                <button className="btn btn-primary" onClick={onExecute}>
                  <Play size={16} />
                  <span>Execute</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};